import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';

const SCENES = [
  {
    n: '01',
    title: 'Optimized Geometric Variety',
    img: '/sticky-scroll-scene-2.webp',
    poster: '/sticky-scroll-scene-2-poster.jpg',
    alt: 'FlexiOss product line — micro-discs, cylinders and structural blocks in varied dimensions',
    prose:
      'Surgical boundaries vary. To eliminate intraoperative modification and structural waste, FlexiOss is distributed across a full matrix of linear dimensions — from precision micro-discs engineered for dental envelopes to long structural cylinders built for segmental long-bone voids.',
    specs: [
      { k: 'Form Factors', v: 'Micro-discs · cylinders · structural blocks' },
      { k: 'Defect Range', v: 'Dental envelopes → segmental voids' },
    ],
  },
  {
    n: '02',
    title: 'Integrity Under Manipulation',
    img: '/sticky-scroll-scene-1.webp',
    poster: '/sticky-scroll-scene-1-poster.jpg',
    alt: 'A FlexiOss cylinder held and turned at an angle by surgical forceps, demonstrating cohesion',
    prose:
      'Legacy substitutes fracture under handling. FlexiOss holds its form through its specialized composition: gripped firmly with forceps or shaped at demanding angles, the material keeps structural cohesion — placing cleanly into the most complex defect zones.',
    specs: [
      { k: 'Behaviour', v: 'Cohesive — no brittle fragmentation' },
      { k: 'Workability', v: 'Cut, bend or contour at any angle' },
    ],
  },
  {
    n: '03',
    title: 'Macroporous System Kinetics',
    img: '/sticky-scroll-scene-3.webp',
    poster: '/sticky-scroll-scene-3-poster.jpg',
    alt: 'Ultra-macro view of a horizontal FlexiOss cylinder showing biphasic interconnected porous channels',
    prose:
      'True regeneration needs a scaffold that hosts native cellular machinery. The horizontal macro-architecture of FlexiOss carries a hyper-precise network of interconnected pores — enabling swift vascularization, uniform fluid distribution and stable localized antibiotic integration.',
    specs: [
      { k: 'Porosity', v: 'Interconnected biomimetic channels' },
      { k: 'Function', v: 'Vascularization · drug-carrier substrate' },
    ],
  },
];

const Scene = ({ scene, index }) => {
  const ref = useRef(null);
  const contentRef = useRef(null);
  const visRef = useRef(null);
  const inView = useInView(contentRef, { once: true, margin: '-120px' });
  const playing = useInView(visRef, { amount: 0.4 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const textY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.16]);
  const reverse = index % 2 === 1;

  const Visual = (
    <div ref={visRef} className={`relative h-[44vh] overflow-hidden lg:h-full ${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
      <motion.img
        key={playing ? 'play' : 'still'}
        src={playing ? scene.img : scene.poster}
        alt={scene.alt}
        loading="lazy"
        style={{ scale: imgScale }}
        className="h-full w-full object-cover"
      />
      <span aria-hidden="true" className="pointer-events-none absolute left-5 top-3 font-display text-[8rem] font-black leading-none text-white/[0.06] lg:text-[11rem]">
        {scene.n}
      </span>
      {/* Cinematic vignette */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#000a14]/40 via-transparent to-[#000a14]/20" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#000a14] via-[#000a14]/40 to-transparent" />
      {/* Watermark cover (bottom-right) */}
      <div
        data-testid={`watermark-cover-${index}`}
        className="pointer-events-none absolute bottom-0 right-0 h-24 w-1/2 bg-gradient-to-tl from-[#000a14] via-[#000a14]/95 to-transparent"
      />
      <div className="pointer-events-none absolute inset-0 border border-white/10" />
    </div>
  );

  const Content = (
    <div ref={contentRef} className={`flex flex-col justify-center px-6 py-14 sm:px-10 lg:px-16 ${reverse ? 'lg:order-1' : 'lg:order-2'}`}>
      <motion.div style={{ y: textY }}>
        <motion.span
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
          className="font-display text-sm font-black tracking-[0.45em] text-blue-400"
        >
          [ {scene.n} ]
        </motion.span>
        <motion.h3
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 font-display text-3xl font-black leading-[1.04] tracking-tight text-white sm:text-4xl lg:text-5xl"
        >
          {scene.title}
        </motion.h3>
        <div className="my-7 h-px w-20 bg-gradient-to-r from-blue-500 to-transparent" />
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="max-w-md text-base font-light leading-relaxed text-slate-400 sm:text-lg"
        >
          {scene.prose}
        </motion.p>
        <div className="mt-10 max-w-md">
          {scene.specs.map((s, i) => (
            <div key={i} className="flex items-baseline justify-between gap-6 border-t border-white/10 py-4">
              <span className="font-display text-[11px] uppercase tracking-[0.22em] text-white/40">{s.k}</span>
              <span className="text-right text-sm font-medium text-slate-200">{s.v}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <section ref={ref} data-testid={`showcase-scene-${index}`} className="relative h-[155vh] bg-[#000a14]">
      <div className="sticky top-0 h-screen overflow-hidden border-t border-white/10">
        <div className="mx-auto grid h-full max-w-[1600px] grid-cols-1 lg:grid-cols-2">
          {Visual}
          {Content}
        </div>
      </div>
    </section>
  );
};

const ProductShowcaseSticky = () => {
  return (
    <div id="showcase" data-testid="product-showcase-sticky">
      {/* Intro */}
      <section className="bg-[#000a14] px-6 pb-2 pt-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1600px]">
          <p className="font-display text-[11px] font-bold uppercase tracking-[0.4em] text-blue-400">
            The Material, In Motion
          </p>
          <h2 className="mt-5 max-w-3xl font-display text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Form. Integrity. Microstructure.
          </h2>
        </div>
      </section>
      {SCENES.map((scene, i) => (
        <Scene key={scene.n} scene={scene} index={i} />
      ))}
    </div>
  );
};

export default ProductShowcaseSticky;
